import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Badge } from "@/components/ui/badge";
import { Check, X, Plus, Package, Unlink, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useEvent } from "@/hooks/useEvents";
import { useEventSpeakers } from "@/hooks/useEventSpeakers";
import { useCreateEvent, useUpdateEvent } from "@/hooks/useEvents";
import { useEventDialog } from "@/contexts/EventDialogContext";
import { useBrandManager } from "@/hooks/useBrandManager";
import { EVENT_STATUS_OPTIONS } from "@/types/events";
import type { EventInsert } from "@/types/events";
import { AVAILABLE_LANGUAGES, parseLanguages } from "@/lib/eventLanguages";
import { RichTextEditor } from "@/components/learn/admin/RichTextEditor";
import { EventImagesInfo } from "@/components/events/EventImagesInfo";
import { EventAdditionalInfo } from "@/components/events/EventAdditionalInfo";
import { EventTestimonialsInfo } from "@/components/events/EventTestimonialsInfo";
import { EventClientLogosInfo } from "@/components/events/EventClientLogosInfo";
import { EventEmailsInfo } from "@/components/events/EventEmailsInfo";
import { DigcompCompetenceSelector } from "@/components/lms/digcomp/DigcompCompetenceSelector";
import { EventMaterialsManager } from "@/components/events/EventMaterialsManager";
import { supabase } from "@/integrations/supabase/client";
import { useEventbriteSync } from "@/hooks/useEventbriteSync";
import { useEventsSettings } from "@/hooks/useEventsSettings";
import { toast } from "sonner";

const defaultForm: Partial<EventInsert> & {
  address?: string;
  time?: string;
  duration?: number | null;
  speaking_languages?: { code: string; name: string }[];
  material_languages?: { code: string; name: string }[];
  digcomp_competence_id?: string | null;
  digcomp_area_id?: string | null;
  digcomp_level_id?: string | null;
  publish_to_eventbrite?: boolean;
  certificate_template_id?: string | null;
} = {
  title: "",
  topic: "",
  description: "",
  status: "planned",
  visible: true,
  speaker_id: null,
  co_speaker_id: null,
  address: "",
  time: "",
  duration: null,
  speaking_languages: [],
  material_languages: [],
  digcomp_competence_id: null,
  digcomp_area_id: null,
  digcomp_level_id: null,
  publish_to_eventbrite: false,
  certificate_template_id: null,
};

export function GlobalEventDialog() {
  const { isOpen, mode, editingEvent, closeDialog } = useEventDialog();
  const { currentBrandId } = useBrandManager();
  const queryClient = useQueryClient();
  const eventId = mode === "edit" && editingEvent ? editingEvent.id : undefined;
  const { data: event } = useEvent(eventId);
  const { speakers } = useEventSpeakers(currentBrandId ?? null);
  const createEvent = useCreateEvent();
  const updateEvent = useUpdateEvent();
  const eventbriteSync = useEventbriteSync();
  const { settings: eventsSettings } = useEventsSettings();
  const [formData, setFormData] = useState(defaultForm);
  const [saving, setSaving] = useState(false);
  const [speakingPopoverOpen, setSpeakingPopoverOpen] = useState(false);
  const [materialPopoverOpen, setMaterialPopoverOpen] = useState(false);
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);
  const [isUnlinkingProduct, setIsUnlinkingProduct] = useState(false);

  const speakingLanguages = formData.speaking_languages ?? [];
  const materialLanguages = formData.material_languages ?? [];

  const brandId = mode === "create" ? currentBrandId ?? undefined : event?.brand_id;
  const isEdit = mode === "edit" && eventId && event;

  // Fetch event certificate templates
  const { data: certificateTemplates = [] } = useQuery({
    queryKey: ['event-certificate-templates', brandId],
    queryFn: async () => {
      if (!brandId) return [];
      const { data, error } = await supabase
        .from('os_events_certificate_templates')
        .select('id, name')
        .eq('brand_id', brandId)
        .order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: !!brandId && isOpen,
  });

  // Fetch linked product
  const { data: linkedProduct } = useQuery({
    queryKey: ['linked-product', event?.product_id],
    queryFn: async () => {
      if (!event?.product_id) return null;
      const { data, error } = await supabase
        .from('brand_products_hooks')
        .select('id, product')
        .eq('id', event.product_id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!event?.product_id,
  });

  const handleCreateProduct = async () => {
    if (!event || !brandId) return;
    setIsCreatingProduct(true);
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('User not authenticated');

      // Create product
      const { data: product, error: productError } = await supabase
        .from('brand_products_hooks')
        .insert({
          brand_id: brandId,
          user_id: user.user.id,
          product: event.title,
          description: event.description || null,
          type: 'Product',
          funnel_stage: 'BofU',
          landing_page_link: null,
          status: 'active',
          images: event.thumbnail ? [event.thumbnail] : null,
        })
        .select()
        .single();

      if (productError) throw productError;

      // Update event with product_id
      const { error: updateError } = await supabase
        .from('os_events')
        .update({ product_id: product.id })
        .eq('id', event.id);

      if (updateError) throw updateError;

      queryClient.invalidateQueries({ queryKey: ['event', event.id] });
      queryClient.invalidateQueries({ queryKey: ['linked-product', product.id] });
      toast.success('Product created and linked successfully');
    } catch (error) {
      console.error('Error creating product:', error);
      toast.error('Failed to create product');
    } finally {
      setIsCreatingProduct(false);
    }
  };

  const handleUnlinkProduct = async () => {
    if (!event) return;
    setIsUnlinkingProduct(true);
    try {
      const { error } = await supabase
        .from('os_events')
        .update({ product_id: null })
        .eq('id', event.id);

      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: ['event', event.id] });
      queryClient.invalidateQueries({ queryKey: ['linked-product'] });
      toast.success('Product unlinked successfully');
    } catch (error) {
      console.error('Error unlinking product:', error);
      toast.error('Failed to unlink product');
    } finally {
      setIsUnlinkingProduct(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    if (event) {
      let speaking = parseLanguages(event.speaking_languages);
      let material = parseLanguages(event.material_languages);
      if (speaking.length === 0 && event.speaking_language) {
        const match = AVAILABLE_LANGUAGES.find((l) => l.code === event.speaking_language);
        speaking = match ? [match] : [{ code: event.speaking_language, name: event.speaking_language }];
      }
      if (material.length === 0 && event.material_language) {
        const match = AVAILABLE_LANGUAGES.find((l) => l.code === event.material_language);
        material = match ? [match] : [{ code: event.material_language, name: event.material_language }];
      }
      setFormData({
        title: event.title,
        topic: event.topic ?? "",
        description: event.description ?? "",
        status: event.status,
        visible: event.visible,
        speaker_id: event.speaker_id ?? null,
        co_speaker_id: event.co_speaker_id ?? null,
        address: event.address ?? "",
        time: event.time ? new Date(event.time).toISOString().slice(0, 16) : "",
        duration: event.duration ?? null,
        speaking_languages: speaking,
        material_languages: material,
        digcomp_competence_id: (event as any).digcomp_competence_id ?? null,
        digcomp_area_id: (event as any).digcomp_area_id ?? null,
        digcomp_level_id: (event as any).digcomp_level_id ?? null,
        publish_to_eventbrite: false,
        certificate_template_id: (event as any).certificate_template_id ?? null,
      });
    } else {
      setFormData({ ...defaultForm, brand_id: brandId! });
    }
  }, [isOpen, event, brandId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!brandId && !eventId) return;
    setSaving(true);
    try {
      const { publish_to_eventbrite, ...rest } = formData;
      const payload = {
        ...rest,
        time: formData.time ? new Date(formData.time).toISOString() : null,
        speaking_languages: formData.speaking_languages ?? [],
        material_languages: formData.material_languages ?? [],
      };
      let savedEventId: string;
      if (isEdit) {
        await updateEvent.mutateAsync({ id: eventId!, updates: payload });
        savedEventId = eventId!;
      } else if (brandId) {
        const created = await createEvent.mutateAsync({ ...payload, brand_id: brandId } as EventInsert);
        savedEventId = created.id;
      } else {
        return;
      }
      if (publish_to_eventbrite && eventsSettings?.eventbrite_oauth_token && eventsSettings?.eventbrite_organization_id && brandId) {
        const action = isEdit && event?.eventbrite_event_id ? "update" : "create";
        eventbriteSync.mutate({ brandId, eventId: savedEventId, action });
      }
      closeDialog();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeDialog()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit Event" : "New Event"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <Tabs defaultValue="basic">
            <TabsList className="flex-wrap h-auto gap-1">
              <TabsTrigger value="basic">Basic</TabsTrigger>
              <TabsTrigger value="physical">Location & Language</TabsTrigger>
              <TabsTrigger value="certificate">Certificate</TabsTrigger>
              {isEdit && event && (
                <>
                  <TabsTrigger value="images">Images</TabsTrigger>
                  <TabsTrigger value="additional">Additional Info</TabsTrigger>
                  <TabsTrigger value="testimonials">Testimonials</TabsTrigger>
                  <TabsTrigger value="client-logos">Client Logos</TabsTrigger>
                  <TabsTrigger value="emails">Email Reminders</TabsTrigger>
                  <TabsTrigger value="materials">Materials</TabsTrigger>
                  <TabsTrigger value="digcomp">DigComp</TabsTrigger>
                  <TabsTrigger value="product">Product</TabsTrigger>
                </>
              )}
            </TabsList>
            <TabsContent value="basic" className="space-y-4 pt-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Event title"
                  required
                />
              </div>
              <div>
                <Label>Topic</Label>
                <Input
                  value={formData.topic ?? ""}
                  onChange={(e) => setFormData({ ...formData, topic: e.target.value })}
                  placeholder="Topic"
                />
              </div>
              <div>
                <Label>Description</Label>
                <RichTextEditor
                  content={formData.description ?? ""}
                  onChange={(v) => setFormData({ ...formData, description: v })}
                  placeholder="Description"
                />
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label>Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(v) => setFormData({ ...formData, status: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EVENT_STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 pt-8">
                  <Switch
                    checked={formData.visible}
                    onCheckedChange={(v) => setFormData({ ...formData, visible: v })}
                  />
                  <Label>Visible (public/embed)</Label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Speaker</Label>
                  <Select
                    value={formData.speaker_id ?? "none"}
                    onValueChange={(v) => setFormData({ ...formData, speaker_id: v === "none" ? null : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select speaker" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {speakers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Co-speaker</Label>
                  <Select
                    value={formData.co_speaker_id ?? "none"}
                    onValueChange={(v) => setFormData({ ...formData, co_speaker_id: v === "none" ? null : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select co-speaker" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {speakers.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {eventsSettings?.eventbrite_oauth_token && eventsSettings?.eventbrite_organization_id && (
                <div className="flex items-center gap-2 pt-2">
                  <Switch
                    checked={formData.publish_to_eventbrite ?? false}
                    onCheckedChange={(v) => setFormData({ ...formData, publish_to_eventbrite: v })}
                  />
                  <Label>Publish to Eventbrite</Label>
                </div>
              )}
            </TabsContent>
            <TabsContent value="certificate" className="space-y-4 pt-4">
              <div>
                <Label>Certificate Template</Label>
                <Select
                  value={formData.certificate_template_id ?? "none"}
                  onValueChange={(v) => setFormData({ ...formData, certificate_template_id: v === "none" ? null : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select certificate template (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (use default)</SelectItem>
                    {certificateTemplates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Template used when issuing attendance certificates for this event
                </p>
              </div>
            </TabsContent>
            <TabsContent value="physical" className="space-y-4 pt-4">
              <div>
                <Label>Address</Label>
                <Textarea
                  value={formData.address ?? ""}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Venue or physical location"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Date & time</Label>
                  <Input
                    type="datetime-local"
                    value={formData.time ?? ""}
                    onChange={(e) => setFormData({ ...formData, time: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Duration (minutes)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={formData.duration ?? ""}
                    onChange={(e) =>
                      setFormData({ ...formData, duration: e.target.value ? parseInt(e.target.value, 10) : null })
                    }
                    placeholder="60"
                  />
                </div>
              </div>
              {/* Speaking Languages */}
              <div className="space-y-3">
                <Label>Speaking languages</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {speakingLanguages.map((lang) => (
                    <Badge key={lang.code} variant="secondary" className="gap-1">
                      {lang.name}
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            speaking_languages: speakingLanguages.filter((l) => l.code !== lang.code),
                          })
                        }
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  <Popover open={speakingPopoverOpen} onOpenChange={setSpeakingPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" size="sm" className="gap-1">
                        <Plus className="h-3 w-3" />
                        Add language
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search languages..." />
                        <CommandList>
                          <CommandEmpty>No language found.</CommandEmpty>
                          <CommandGroup>
                            {AVAILABLE_LANGUAGES.map((lang) => (
                              <CommandItem
                                key={lang.code}
                                value={lang.name}
                                onSelect={() => {
                                  if (!speakingLanguages.find((l) => l.code === lang.code)) {
                                    setFormData({
                                      ...formData,
                                      speaking_languages: [...speakingLanguages, lang],
                                    });
                                  }
                                  setSpeakingPopoverOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    speakingLanguages.find((l) => l.code === lang.code)
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                                {lang.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <p className="text-sm text-muted-foreground">
                  Languages the event is delivered in (e.g. presentation language)
                </p>
              </div>

              {/* Material Languages */}
              <div className="space-y-3">
                <Label>Material languages</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {materialLanguages.map((lang) => (
                    <Badge key={lang.code} variant="secondary" className="gap-1">
                      {lang.name}
                      <button
                        type="button"
                        onClick={() =>
                          setFormData({
                            ...formData,
                            material_languages: materialLanguages.filter((l) => l.code !== lang.code),
                          })
                        }
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                  <Popover open={materialPopoverOpen} onOpenChange={setMaterialPopoverOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" variant="outline" size="sm" className="gap-1">
                        <Plus className="h-3 w-3" />
                        Add language
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0" align="start">
                      <Command>
                        <CommandInput placeholder="Search languages..." />
                        <CommandList>
                          <CommandEmpty>No language found.</CommandEmpty>
                          <CommandGroup>
                            {AVAILABLE_LANGUAGES.map((lang) => (
                              <CommandItem
                                key={lang.code}
                                value={lang.name}
                                onSelect={() => {
                                  if (!materialLanguages.find((l) => l.code === lang.code)) {
                                    setFormData({
                                      ...formData,
                                      material_languages: [...materialLanguages, lang],
                                    });
                                  }
                                  setMaterialPopoverOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-4 w-4",
                                    materialLanguages.find((l) => l.code === lang.code)
                                      ? "opacity-100"
                                      : "opacity-0"
                                  )}
                                />
                                {lang.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                <p className="text-sm text-muted-foreground">
                  Languages of handouts, slides, or other materials
                </p>
              </div>
            </TabsContent>

            {isEdit && event && brandId && (
              <>
                <TabsContent value="images" className="space-y-4 pt-4">
                  <EventImagesInfo event={event} brandId={brandId} />
                </TabsContent>
                <TabsContent value="additional" className="space-y-4 pt-4">
                  <EventAdditionalInfo event={event} brandId={brandId} />
                </TabsContent>
                <TabsContent value="testimonials" className="space-y-4 pt-4">
                  <EventTestimonialsInfo event={event} brandId={brandId} />
                </TabsContent>
                <TabsContent value="client-logos" className="space-y-4 pt-4">
                  <EventClientLogosInfo event={event} brandId={brandId} />
                </TabsContent>
                <TabsContent value="emails" className="space-y-4 pt-4">
                  <EventEmailsInfo event={event} brandId={brandId} />
                </TabsContent>
                <TabsContent value="materials" className="space-y-4 pt-4">
                  <EventMaterialsManager event={event} />
                </TabsContent>
                <TabsContent value="digcomp" className="space-y-4 pt-4">
                  <DigcompCompetenceSelector
                    brandId={brandId}
                    competenceId={formData.digcomp_competence_id ?? null}
                    areaId={formData.digcomp_area_id ?? null}
                    levelId={formData.digcomp_level_id ?? null}
                    onCompetenceChange={(competenceId, areaId) => {
                      setFormData({
                        ...formData,
                        digcomp_competence_id: competenceId,
                        digcomp_area_id: areaId,
                      });
                    }}
                    onLevelChange={(levelId) => {
                      setFormData({ ...formData, digcomp_level_id: levelId });
                    }}
                    showLevelSelector
                  />
                </TabsContent>
                <TabsContent value="product" className="space-y-4 pt-4">
                  <div>
                    <Label>Linked Product</Label>
                    <p className="text-xs text-muted-foreground mb-4">
                      Create a product from this event to enable sales tracking
                    </p>
                    {event.product_id && linkedProduct ? (
                      <div className="flex items-center gap-3 p-4 border rounded-lg bg-muted/50">
                        <Package className="h-5 w-5 text-muted-foreground" />
                        <div className="flex-1">
                          <p className="font-medium">{linkedProduct.product}</p>
                          <p className="text-xs text-muted-foreground">Product ID: {linkedProduct.id}</p>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleUnlinkProduct}
                          disabled={isUnlinkingProduct}
                        >
                          {isUnlinkingProduct ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Unlink className="h-4 w-4 mr-2" />
                              Unlink Product
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCreateProduct}
                        disabled={isCreatingProduct}
                      >
                        {isCreatingProduct ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Package className="h-4 w-4 mr-2" />
                        )}
                        Create Product
                      </Button>
                    )}
                  </div>
                </TabsContent>
              </>
            )}
          </Tabs>
          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? "Saving..." : isEdit ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
